import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { UserPermissions } from '@eview/core/models/user-permissions';
import { Roles, RoleItem } from '@eview/core/models/roles';
import { selectRoles } from '@eview/core/store/selectors/roles.selector';
import { selectUserPermissions } from '@eview/core/store/selectors/user-permissions.selector';
import { AppState } from '@eview/core/store/states/app.state';
import { Observable, Subject, of } from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { selectUser } from '@eview/core/store/selectors/user.selector';
import { isEmpty } from 'lodash';
import { UserService } from '@eview/core/users/user.service';
import { AuthCatalogFacade } from '@eview/core/store/facades/auth-catalog.facade';
import { User, ListUserOptions } from '@eview/core/models/user';

@Component({
  selector: 'eview-user-management',
  templateUrl: 'index.component.html',
  styleUrls: ['index.component.scss'],
})
export class UserManagementComponent implements OnInit, OnDestroy {
  listOption: ListUserOptions;
  loggedinUser: User;
  user: User;
  pending_total_count = 0;
  pendingCountLoading = false;
  activeNavId = 1;

  userPermissions$: Observable<UserPermissions>;
  permissions: string[][] = [];
  roles$: Observable<Roles>;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private userService: UserService,
    private authCatalog: AuthCatalogFacade
  ) {
    this.userPermissions$ = this.store.select(selectUserPermissions);
    this.roles$ = this.store.select(selectRoles);
  }

  ngOnInit() {
    this.authCatalog.ensureAuthCatalog();

    this.store
      .select(selectRoles)
      .pipe(
        map(data => data?.results),
        takeUntil(this.destroy$)
      )
      .subscribe(data => {
        if (data) {
          data.forEach(val => {
            const temp = [];
            if (val && !isEmpty(val.permissions)) {
              val.permissions.forEach(permission => {
                temp[permission] = true;
              });
            }
            this.permissions[val.name] = temp;
          });
        }
      });

    this.store
      .select(selectUser)
      .pipe(
        filter((user): user is User => !!user),
        take(1),
        switchMap(user => {
          this.user = user;
          if (user.role === 'super' || user.role === 'admin') {
            this.pendingCountLoading = true;
            return this.userService.list({
              offset: 0,
              limit: 1,
              orderby: 'realname',
              active: 0,
              include_contacts: 0
            });
          }
          return of(null);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(data => {
        this.pendingCountLoading = false;
        if (data) {
          this.pending_total_count = data.total_count;
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get showPendingTab(): boolean {
    return !!this.user && (this.user.role === 'super' || this.user.role === 'admin');
  }

  updatePendingCount(count: number) {
    this.pending_total_count = count;
  }

  refreshPendingCount() {
    if (!this.user || (this.user.role !== 'super' && this.user.role !== 'admin')) {
      return;
    }
    this.pendingCountLoading = true;
    this.userService
      .list({
        offset: 0,
        limit: 1,
        orderby: 'realname',
        active: 0,
        include_contacts: 0
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        data => {
          this.pendingCountLoading = false;
          this.pending_total_count = data?.total_count ?? 0;
        },
        () => {
          this.pendingCountLoading = false;
        }
      );
  }

  onValueChange(role: RoleItem) {
    const selectValues = this.permissions[role.name];
    const permission: string[] = [];
    Object.keys(selectValues).forEach(item => {
      if (selectValues[item]) {
        permission.push(item);
      }
    });
    const reqBody = { ...role, permissions: permission as unknown as RoleItem['permissions'] };
    this.userService.updateRoles(reqBody as RoleItem, role.id).subscribe(() => {
      this.authCatalog.ensureRoles(true);
    });
  }
}
